/**
 * Essays list page component.
 * 
 * Features:
 * - Displays all essays in card format
 * - Status filter (All, Draft, Published, etc.)
 * - Category filter (Phase 4)
 * - Tag filter (Phase 4)
 * - Loading and error states
 * - Empty state when no essays
 * 
 * Called by:
 * - App.tsx as route for /essays
 */
import { useState } from 'react';
import { Typography, Row, Col, Segmented, Empty, Spin, Alert, Select, Space } from 'antd';
import { FolderOutlined, TagsOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getEssays, getCategories, getTags } from '../services/api';
import EssayCard from '../components/EssayCard';
import type { EssayStatus } from '@compendium/shared';

const { Title, Text } = Typography;

/**
 * Filter options for essay status.
 */
type StatusFilter = 'all' | EssayStatus;

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'In Review', value: 'in_review' },
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

/**
 * Essays list page component.
 * Displays all essays with optional status, category, and tag filtering.
 */
function EssaysPage() {
  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [tagFilter, setTagFilter] = useState<number | null>(null);

  // Fetch essays from API
  const {
    data: essays = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['essays', statusFilter],
    queryFn: () => getEssays(statusFilter === 'all' ? undefined : statusFilter),
  });

  // Fetch categories for filter dropdown (Phase 4)
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  // Fetch tags for filter dropdown (Phase 4)
  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: getTags,
  });

  /**
   * Handle status filter change.
   */
  function handleFilterChange(value: string | number) {
    setStatusFilter(value as StatusFilter);
  }

  // Filter essays by category and tag (client-side for simplicity)
  // Note: For production, this should be done server-side
  const filteredEssays = essays.filter((essay) => {
    if (categoryFilter !== null && essay.categoryId !== categoryFilter) {
      return false;
    }
    // Tag filtering would require additional data - skipping for now
    return true;
  });

  // Category options for filter
  const categoryOptions = categories.map((cat) => ({ value: cat.id, label: cat.name }));

  // Tag options for filter (would need essay-tag data to filter properly)
  const tagOptions = tags.map((tag) => ({ value: tag.id, label: tag.name }));

  return (
    <div>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <span style={{ borderBottom: '2px solid #d4af37', paddingBottom: 4 }}>Essays</span>
        </Title>

        {/* Status filter */}
        <Segmented
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={handleFilterChange}
        />
      </div>

      {/* Category and Tag filters (Phase 4) */}
      <div style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Space>
            <FolderOutlined style={{ color: '#d4af37' }} />
            <Select
              placeholder="Category"
              value={categoryFilter ?? undefined}
              onChange={(value) => setCategoryFilter(value ?? null)}
              options={categoryOptions}
              style={{ width: 150 }}
              allowClear
            />
          </Space>
          <Space>
            <TagsOutlined style={{ color: '#d4af37' }} />
            <Select
              placeholder="Tag"
              value={tagFilter ?? undefined}
              onChange={(value) => setTagFilter(value ?? null)}
              options={tagOptions}
              style={{ width: 150 }}
              allowClear
            />
          </Space>
          {(categoryFilter !== null || tagFilter !== null) && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              Showing {filteredEssays.length} of {essays.length} essays
            </Text>
          )}
        </Space>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spin size="large" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <Alert
          message="Error loading essays"
          description={error instanceof Error ? error.message : 'An unknown error occurred'}
          type="error"
          showIcon
        />
      )}

      {/* Empty state */}
      {!isLoading && !error && filteredEssays.length === 0 && (
        <Empty
          description={
            essays.length === 0
              ? 'No essays found. Create your first essay by adding a folder with an essay.md file to the /essays/ directory.'
              : categoryFilter || tagFilter
              ? 'No essays match the selected filters.'
              : `No ${statusFilter.replace('_', ' ')} essays found.`
          }
          style={{ padding: '48px' }}
        />
      )}

      {/* Essay grid */}
      {!isLoading && !error && filteredEssays.length > 0 && (
        <Row gutter={[24, 24]}>
          {filteredEssays.map((essay) => (
            <Col key={essay.id} xs={24} sm={12} lg={8}>
              <EssayCard essay={essay} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

export default EssaysPage;
